import { useEffect, useState } from "react"
import config from "react-global-configuration"

import { request } from "lib/request"
import { Admin } from "types/entity"

import { MSS_EMAIL_PATTERN } from "./MssEmailEntry"

// The RPPS directory lookup also returns the MSS address known for the RPPS
async function rppsMssApi(
  rpps: string,
): Promise<{ status: string; data?: { mss_email?: string | null } }> {
  return request(config.get("admin.rpps"), {
    method: "POST",
    payload: { rpps },
  })
}

export interface DirectoryMss {
  // Address the RPPS directory knows for the pharmacy owner, null when none or not looked up
  email: string | null
  // True while the lookup is running: the MSS address must not be edited before it settles
  loading: boolean
}

// MSS address the RPPS directory knows for the pharmacy owner, looked up once per modal session
// (enabled) and only while the pharmacy has not entered an address itself
export const useDirectoryMss = (admin: Admin | undefined, enabled: boolean): DirectoryMss => {
  const [email, setEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setEmail(null)
    const rpps = admin?.rpps
    if (!enabled || !rpps || admin?.mss_email) return
    let cancelled = false
    setLoading(true)
    rppsMssApi(rpps)
      .then((response) => {
        const found = response?.status === "ok" ? response.data?.mss_email?.trim() : undefined
        // Only an address the MSS rule accepts is proposed: a bridged mailbox (Apicrypt) the
        // pharmacist could never confirm is treated as no address
        if (!cancelled && found && MSS_EMAIL_PATTERN.test(found)) setEmail(found)
      })
      .catch((error) => console.error(error, { route: config.get("admin.rpps") }))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      setLoading(false)
    }
  }, [enabled, admin?.rpps, admin?.mss_email])
  return { email, loading }
}

// Address shown for the pharmacy: the entered one first, otherwise the directory one
export const knownMssEmail = (admin: Admin | undefined, directory: DirectoryMss): string | undefined =>
  admin?.mss_email || directory.email || undefined
